import Link from "@/components/school/SchoolLink";
import { useStreak } from "@/hooks/useStreak";
import { createLogger } from "@/lib/logger";
import { buildTrackedPaywallUrl } from "@/lib/paywallUrl";
import { supabase } from "@/lib/supabaseClient";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  BookMarked,
  CreditCard,
  Download,
  GraduationCap,
  HelpCircle,
  LogOut,
  Menu,
  MessageSquare,
  MessageSquarePlus,
  Rocket,
  Share2,
  TrendingUp,
  Trophy,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import React from "react";
import { useAuth } from "./AuthContext";
import StreakDisplay from "./StreakDisplay";
import UserLevelDisplay from "./UserLevelDisplay";

const logger = createLogger("HamburgerMenu");

interface HamburgerMenuProps {
  onFeedbackClick?: () => void; // Now optional - feedback redirects to Canny
  onHelpClick: () => void;
  onShareClick: () => void;
  onLeaderboardClick?: () => void;
  onPastConversationsClick?: () => void;
  onClassroomClick?: () => void;
  userEmail?: string | null;
  userLevel?: any;
  loading: boolean;
  subscriptionManagementUrl?: string | null; // RevenueCat billing portal URL
  hasSubscriptionAccess?: boolean;
  brandVariant?: "default" | "ecole" | "liceo";
}

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({
  onFeedbackClick,
  onHelpClick,
  onShareClick,
  onLeaderboardClick,
  onPastConversationsClick,
  onClassroomClick,
  userEmail,
  userLevel,
  loading,
  subscriptionManagementUrl,
  hasSubscriptionAccess = false,
  brandVariant = "default",
}) => {
  const { user } = useAuth();
  const posthog = usePostHog();
  const { streakDisplay, loading: streakLoading } = useStreak(user?.id || null);
  const [deferredPrompt, setDeferredPrompt] = React.useState<any>(null);

  // Handle PWA install prompt
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      logger.info("PWA installed successfully");
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  // Debug: Log the management URL
  React.useEffect(() => {
    logger.info("HamburgerMenu received management URL", {
      subscriptionManagementUrl,
      hasUrl: !!subscriptionManagementUrl,
      hasSubscriptionAccess,
      urlType: typeof subscriptionManagementUrl,
      urlValue: subscriptionManagementUrl,
      willShow: hasSubscriptionAccess || !!subscriptionManagementUrl,
    });
  }, [hasSubscriptionAccess, subscriptionManagementUrl]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const itemClass =
    "group flex min-h-10 cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-white/75 outline-none transition-colors hover:bg-white/[0.06] hover:text-white focus:bg-white/[0.08]";
  const sectionLabelClass = "px-3 pb-1 pt-3 text-[11px] font-semibold uppercase text-white/35";
  const menuItemIconClass =
    "text-white/45 transition-colors group-hover:text-orange-300 group-focus:text-orange-300";
  const betaBadgeClass =
    "rounded-md border border-white/10 bg-white/[0.05] px-1.5 py-0.5 text-[10px] font-semibold text-white/50";
  const hasPastConversationsItem = Boolean(onPastConversationsClick && user);
  const hasLeaderboardItem = Boolean(onLeaderboardClick);
  const practiceSectionClass = hasPastConversationsItem ? "" : "md:hidden";

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={`flex h-10 w-10 items-center justify-center rounded-lg border border-white/12 bg-black/20 text-white/75 transition-colors hover:border-white/25 hover:bg-white/[0.08] hover:text-white focus-visible:outline-none focus-visible:ring-2 ${
            brandVariant !== "default"
              ? "focus-visible:ring-school-focus"
              : "focus-visible:ring-orange-300"
          }`}
          aria-label="Open menu"
        >
          <Menu size={18} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[9999] max-h-[80vh] min-w-[260px] overflow-y-auto rounded-lg border border-white/10 bg-[#121318] p-2 text-sm text-white shadow-[0_24px_70px_rgba(0,0,0,0.55)] focus:outline-none"
          sideOffset={8}
          align="end"
        >
          {/* User Level and Streak Display for mobile/small screens */}
          {userEmail && (
            <div className="mb-2 space-y-2 border-b border-white/10 px-2 pb-3">
              <UserLevelDisplay
                userEmail={userEmail}
                userLevel={userLevel}
                loading={loading}
                showHoverDetails={false}
                usePortalForModal={true}
              />
              <StreakDisplay
                streakDisplay={streakDisplay}
                loading={streakLoading}
                showHoverDetails={false}
                usePortalForModal={true}
              />
            </div>
          )}

          {(hasLeaderboardItem || hasPastConversationsItem) && (
            <div className={practiceSectionClass}>
              <div className={sectionLabelClass}>Practice</div>
              {hasPastConversationsItem && (
                <DropdownMenu.Item onSelect={onPastConversationsClick} className={itemClass}>
                  <MessageSquarePlus size={18} className={menuItemIconClass} />
                  <span>Past Conversations</span>
                </DropdownMenu.Item>
              )}

              {onLeaderboardClick && (
                <DropdownMenu.Item
                  onSelect={onLeaderboardClick}
                  className={hasPastConversationsItem ? `${itemClass} md:hidden` : itemClass}
                >
                  <Trophy size={18} className={menuItemIconClass} />
                  <span>Leaderboard</span>
                </DropdownMenu.Item>
              )}
            </div>
          )}

          {user && (
            <>
              <DropdownMenu.Separator className="my-2 h-px bg-white/[0.08]" />
              <div className={sectionLabelClass}>Learning</div>
              <DropdownMenu.Item asChild>
                <Link href="/flashcards" className={itemClass}>
                  <BookMarked size={18} className={menuItemIconClass} />
                  <span>My Flashcards</span>
                </Link>
              </DropdownMenu.Item>

              <DropdownMenu.Item asChild>
                <Link href="/progress" className={itemClass}>
                  <TrendingUp size={18} className={menuItemIconClass} />
                  <span>Progress</span>
                </Link>
              </DropdownMenu.Item>

              {onClassroomClick && (
                <DropdownMenu.Item onSelect={onClassroomClick} className={itemClass}>
                  <GraduationCap size={18} className={menuItemIconClass} />
                  <span className="flex items-center gap-2">
                    Classroom
                    <span className={betaBadgeClass}>BETA</span>
                  </span>
                </DropdownMenu.Item>
              )}
            </>
          )}

          <DropdownMenu.Separator className="my-2 h-px bg-white/[0.08]" />
          <div className={sectionLabelClass}>Teach</div>
          <DropdownMenu.Item asChild>
            <Link href={user ? "/tutor-studio" : "/tutors"} className={itemClass}>
              <GraduationCap size={18} className={menuItemIconClass} />
              <span>{user ? "Tutor Studio" : "For tutors"}</span>
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="my-2 h-px bg-white/[0.08]" />
          <div className={sectionLabelClass}>Account</div>

          {user && !hasSubscriptionAccess && (
            <DropdownMenu.Item asChild>
              <Link
                href={buildTrackedPaywallUrl({ source: "hamburger_menu" })}
                className={itemClass}
              >
                <Rocket size={18} className={menuItemIconClass} />
                <span>Upgrade</span>
              </Link>
            </DropdownMenu.Item>
          )}

          {(hasSubscriptionAccess || subscriptionManagementUrl) && (
            <DropdownMenu.Item asChild>
              <Link href="/subscription" className={itemClass}>
                <CreditCard size={18} className={menuItemIconClass} />
                <span>Manage Subscription</span>
              </Link>
            </DropdownMenu.Item>
          )}

          <DropdownMenu.Item asChild>
            <a
              href="https://play.google.com/store/apps/details?id=com.chickytutor"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                posthog?.capture("google_play_badge_clicked", {
                  placement: "hamburger_menu",
                  app_package: "com.chickytutor",
                })
              }
              className={itemClass}
            >
              <Download size={18} className={menuItemIconClass} />
              <span className="flex items-center gap-2">
                Get the Android app
                <span className={betaBadgeClass}>NEW</span>
              </span>
            </a>
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="my-2 h-px bg-white/[0.08]" />
          <div className={sectionLabelClass}>Support</div>

          {onFeedbackClick ? (
            <DropdownMenu.Item onSelect={onFeedbackClick} className={itemClass}>
              <MessageSquare size={18} className={menuItemIconClass} />
              <span>Feedback & bugs</span>
            </DropdownMenu.Item>
          ) : (
            <DropdownMenu.Item asChild>
              <a
                href="https://chickytutor.canny.io/"
                target="_blank"
                rel="noopener noreferrer"
                className={itemClass}
              >
                <MessageSquare size={18} className={menuItemIconClass} />
                <span>Feedback & bugs</span>
              </a>
            </DropdownMenu.Item>
          )}

          <DropdownMenu.Item onSelect={onHelpClick} className={itemClass}>
            <HelpCircle size={18} className={menuItemIconClass} />
            <span>Help</span>
          </DropdownMenu.Item>

          <DropdownMenu.Item onSelect={onShareClick} className={itemClass}>
            <Share2 size={18} className={menuItemIconClass} />
            <span>Share</span>
          </DropdownMenu.Item>

          {user && (
            <>
              <DropdownMenu.Separator className="my-2 h-px bg-white/[0.08]" />
              <DropdownMenu.Item
                onSelect={handleSignOut}
                className="group flex min-h-10 cursor-pointer items-center gap-3 rounded-md border border-red-400/20 px-3 py-2.5 text-sm font-medium text-red-300 outline-none transition-colors hover:bg-red-500/10 hover:text-red-200 focus:bg-red-500/10"
              >
                <LogOut size={18} />
                <span>Sign Out</span>
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default HamburgerMenu;
